import { useEffect, useRef, useState, type MutableRefObject } from 'react';
import type { StationSnapshot, Track } from './types';
import { api } from './api';
import { ls } from './storage';

interface OfflineEvent {
  uid: string;
  exit: 'ended' | 'skip';
  at: number;
}

const KEY = 'claudio.offline.events';

function loadEvents(): OfflineEvent[] {
  try {
    const v = JSON.parse(ls.get(KEY) ?? '[]');
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}

function saveEvents(evs: OfflineEvent[]): void {
  ls.set(KEY, JSON.stringify(evs.slice(-20)));
}

async function inLocalCache(videoId: string): Promise<boolean> {
  try {
    return !!(await caches.match(`/stream/${videoId}`));
  } catch {
    return false;
  }
}

export interface OfflineBridge {
  snap: StationSnapshot;
  active: boolean;
  localAdvance: (exit: 'ended' | 'skip') => Promise<boolean>;
  elapsedRef: MutableRefObject<() => number>;
}

/** 断网兜底: 服务器够不着时本地切到下一首已存在本机的歌, 切换事件落盘;
 *  回网后按序补给服务器推进电台(不写收听统计), 若电台已被别处推进则服务器为准。 */
export function useOffline(snap: StationSnapshot, connected: boolean): OfflineBridge {
  const [overlay, setOverlay] = useState<{ track: Track; startedAt: number } | null>(null);
  const elapsedRef = useRef<() => number>(() => 0);
  const snapRef = useRef(snap);
  snapRef.current = snap;
  const overlayRef = useRef(overlay);
  overlayRef.current = overlay;

  // 服务器追上本地位置(同步成功或别处推进到同一首) → 撤覆盖层, 回到服务器时间线
  useEffect(() => {
    if (overlay && snap.nowPlaying?.track.uid === overlay.track.uid) setOverlay(null);
  }, [snap.nowPlaying?.track.uid, overlay]);

  const localAdvance = async (exit: 'ended' | 'skip'): Promise<boolean> => {
    const s = snapRef.current;
    const cur = overlayRef.current?.track ?? s.nowPlaying?.track;
    if (!cur) return false;
    const from = overlayRef.current ? s.queue.findIndex((t) => t.uid === overlayRef.current!.track.uid) + 1 : 0;
    for (const t of s.queue.slice(Math.max(0, from))) {
      if (!t.videoId || t.status === 'failed') continue;
      if (!(await inLocalCache(t.videoId))) continue;
      saveEvents([...loadEvents(), { uid: cur.uid, exit, at: Date.now() }]);
      setOverlay({ track: { ...t, file: `/stream/${t.videoId}` }, startedAt: Date.now() });
      return true;
    }
    return false;
  };

  const syncing = useRef(false);
  useEffect(() => {
    if (!connected) return;
    const sync = () => {
      if (syncing.current) return;
      const evs = loadEvents();
      if (!evs.length) return;
      syncing.current = true;
      api
        .offlineSync(evs, Math.round(elapsedRef.current()))
        .then((r) => {
          saveEvents([]);
          // 服务器没接受(被别处推进过) → 立即回它的时间线; 接受了则等状态推送时撤层, 不闪切
          if (r.nowPlaying?.track.uid !== overlayRef.current?.track.uid) setOverlay(null);
        })
        .catch(() => {})
        .finally(() => {
          syncing.current = false;
        });
    };
    sync();
    const t = window.setInterval(sync, 10_000);
    return () => window.clearInterval(t);
  }, [connected]);

  const eff: StationSnapshot = overlay
    ? {
        ...snap,
        status: 'playing',
        nowPlaying: { track: overlay.track, startedAt: overlay.startedAt },
        queue: snap.queue.slice(snap.queue.findIndex((t) => t.uid === overlay.track.uid) + 1),
      }
    : snap;
  return { snap: eff, active: !!overlay, localAdvance, elapsedRef };
}
